import { useState, useEffect } from "react"
import { Form, Input, Select, Grid, message, theme, Button, Space } from "antd"
import { UserRole, UserStatus } from "../../../shared/enums"
import { CreateUserDTO, UpdateUserDTO, User } from "../services/superadmin.service"
import { supabase } from "@/integrations/supabase/client"
import { UserOutlined, MailOutlined, LockOutlined } from "@ant-design/icons"
import {
  countActiveUsersInTenant,
  fetchTenantTipoUnidade,
  getAssignableRolesForTenant,
  isRoleLockedForTenant,
  resolveRoleForTenant,
  resolveRoleForTenantId,
  type TenantTipoUnidade,
} from '@/features/superadmin/utils/tenantRoleResolver';
import { getRoleLabel } from '@/features/auth/utils/roleLabels';
import { UserDialogShell } from './UserDialogShell';
import { generateUsername } from '@/utils/generateUsername';

interface UserDialogProps {
  mode: "create" | "edit"
  user?: User
  userUUID?: string
  onSubmit: (data: CreateUserDTO | UpdateUserDTO) => Promise<void>
  trigger?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  tenants?: Array<{ id: string; nome: string }>
}

export function UserDialog({ mode, user, userUUID, onSubmit, trigger, open, onOpenChange, tenants = [] }: UserDialogProps) {
  const [form] = Form.useForm();
  const [internalOpen, setInternalOpen] = useState(false)
  const [loading, setLoading] = useState(false);
  const { token } = theme.useToken();

  // Estados para supervisores
  const [supervisores, setSupervisores] = useState<any[]>([])
  const [isLoadingSupervisores, setIsLoadingSupervisores] = useState(false)
  const [selectedSupervisor, setSelectedSupervisor] = useState<number | null>(null)
  const [isLoadingUserSupervisor, setIsLoadingUserSupervisor] = useState(false)
  const [tenantTipo, setTenantTipo] = useState<TenantTipoUnidade | null>(null)
  const [activeUsersInTenant, setActiveUsersInTenant] = useState(0)
  const [roleLocked, setRoleLocked] = useState(false)

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  // Controlar o estado do diálogo
  const isControlled = open !== undefined && onOpenChange !== undefined
  const dialogOpen = isControlled ? open : internalOpen
  const setDialogOpen = isControlled ? onOpenChange : setInternalOpen

  // Monitorar nome e sobrenome para gerar username automaticamente
  const firstName = Form.useWatch('first_name', form);
  const lastName = Form.useWatch('last_name', form);
  const currentUsername = Form.useWatch('username', form);
  const [usernameWasManuallyChanged, setUsernameWasManuallyChanged] = useState(false);

  // Função para carregar o supervisor atual do usuário (modo edição)
  const carregarSupervisorAtual = async (userId: string, tenantId: string | null) => {
    if (!tenantId || !userId) return

    setIsLoadingUserSupervisor(true)
    try {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      if (!uuidRegex.test(userId)) {
        setSelectedSupervisor(null)
        return
      }

      const { data: consultor, error: consultorError } = await supabase
        .from('consultores')
        .select('id, supervisor_id, tipo, status')
        .eq('user_id', userId)
        .eq('status', 'ativo')
        .maybeSingle()

      if (consultorError) {
        console.warn('Busca de consultor retornou erro ou vazio:', consultorError)
        setSelectedSupervisor(null)
        return
      }

      if (consultor && consultor.supervisor_id) {
        setSelectedSupervisor(consultor.supervisor_id)
        form.setFieldValue('supervisor_id', consultor.supervisor_id)
      } else {
        setSelectedSupervisor(null)
        form.setFieldValue('supervisor_id', undefined)
      }
    } catch (error) {
      console.error('Erro ao carregar supervisor atual:', error)
      setSelectedSupervisor(null)
    } finally {
      setIsLoadingUserSupervisor(false)
    }
  }

  // Função para carregar supervisores do sistema
  const carregarSupervisores = async () => {
    setIsLoadingSupervisores(true)
    try {
      const { data: supervisoresData, error } = await supabase
        .from('consultores')
        .select('id, nome, tenant_id')
        .eq('tipo', 'supervisor')
        .eq('status', 'ativo')
        .order('nome')

      if (error) {
        console.error('Erro ao carregar supervisores:', error)
        setSupervisores([])
        return
      }

      setSupervisores(supervisoresData || [])
    } catch (error) {
      console.error('Erro ao carregar supervisores:', error)
      setSupervisores([])
    } finally {
      setIsLoadingSupervisores(false)
    }
  }

  // Sincronizar formulário quando o diálogo abrir ou o usuário mudar
  useEffect(() => {
    if (dialogOpen) {
      if (mode === "edit" && user) {
        form.setFieldsValue({
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          username: user.username,
          role: user.role,
          status: user.status,
          tenant_id: user.tenant_id ? user.tenant_id.toString() : "",
        });
        setUsernameWasManuallyChanged(false);
        carregarSupervisores();
        carregarSupervisorAtual(user.id, user.tenant_id ? user.tenant_id.toString() : null);
      } else {
        form.resetFields();
        setUsernameWasManuallyChanged(false);
        carregarSupervisores(); // Carregar para novos usuários também
        form.setFieldsValue({
          role: undefined,
          status: UserStatus.ACTIVE,
          tenant_id: "",
          supervisor_id: undefined,
        });
        setSelectedSupervisor(null);
      }
    }
  }, [dialogOpen, mode, user, userUUID, form]);

  // Gerar username automaticamente quando nome/sobrenome mudam
  useEffect(() => {
    if (dialogOpen && mode === "create" && !usernameWasManuallyChanged) {
      if (firstName || lastName) {
        const generated = generateUsername(firstName, lastName);
        if (generated) {
          form.setFieldValue('username', generated);
        }
      }
    }
  }, [firstName, lastName, dialogOpen, mode, form, usernameWasManuallyChanged]);

  const currentRole = Form.useWatch('role', form);
  const currentTenantId = Form.useWatch('tenant_id', form);

  useEffect(() => {
    const syncRoleForTenant = async () => {
      if (!currentTenantId || currentTenantId === '') {
        setTenantTipo(null);
        setActiveUsersInTenant(0);
        setRoleLocked(false);
        return;
      }

      try {
        const [tipo, count] = await Promise.all([
          fetchTenantTipoUnidade(currentTenantId),
          countActiveUsersInTenant(currentTenantId),
        ]);
        setTenantTipo(tipo);
        setActiveUsersInTenant(count);

        const locked = isRoleLockedForTenant(tipo, count);
        setRoleLocked(locked);

        if (mode === 'create') {
          if (locked) {
            form.setFieldValue('role', resolveRoleForTenant(tipo, {
              requestedRole: form.getFieldValue('role'),
              activeUsersInTenant: count,
            }));
          } else if (!getAssignableRolesForTenant(tipo, count).includes(form.getFieldValue('role'))) {
            // Perfil escolhido não vale para a nova empresa: obriga nova escolha
            form.setFieldValue('role', undefined);
          }
        }
      } catch (error) {
        console.error('Erro ao resolver role do tenant:', error);
      }
    };

    if (dialogOpen) {
      syncRoleForTenant();
    }
  }, [currentTenantId, dialogOpen, mode, form]);

  const assignableRoles = getAssignableRolesForTenant(tenantTipo, activeUsersInTenant);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = { ...values };
      const rolesComSupervisor = [UserRole.LIDER_VENDAS, UserRole.RELACIONADOR];

      if (payload.tenant_id === "") {
        payload.tenant_id = null;
      }

      if (payload.tenant_id) {
        payload.role = await resolveRoleForTenantId(payload.tenant_id, values.role);
      }

      if (rolesComSupervisor.includes(values.role)) {
        const supervisorId = values.supervisor_id ?? selectedSupervisor;
        if (!supervisorId) {
          message.error('Selecione o supervisor do time para este perfil.');
          setLoading(false);
          return;
        }
        payload.supervisorId = supervisorId;
      } else if ([UserRole.USER, UserRole.ASSOCIADO, UserRole.REPRESENTANTE, UserRole.SUPER_ADMIN, UserRole.SECRETARIA, UserRole.ADMIN, UserRole.POS_VENDA].includes(values.role)) {
        payload.supervisorId = null;
      } else if (selectedSupervisor && values.role === UserRole.SUPERVISOR) {
        payload.supervisorId = selectedSupervisor;
      }

      delete payload.supervisor_id;

      await onSubmit(payload);
      setDialogOpen(false);
      message.success(mode === 'create' ? 'Membro criado com sucesso!' : 'Membro atualizado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao salvar usuário:', error);
      if (error?.errorFields) return;
      const errorMsg = error.message || 'Erro desconhecido ao processar solicitação.';
      message.error(`Não foi possível salvar: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {trigger && (
        <span onClick={() => setDialogOpen(true)} style={{ cursor: 'pointer' }}>
          {trigger}
        </span>
      )}
      <UserDialogShell
        open={dialogOpen}
        onCancel={() => setDialogOpen(false)}
        eyebrow={mode === "create" ? "Adicionar Membro" : "Editar Membro"}
        title={mode === "create" ? "Novo membro na plataforma" : "Atualize os dados do membro"}
        width={isMobile ? undefined : 600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: UserStatus.ACTIVE,
            tenant_id: ""
          }}
          style={{
            '--form-item-margin-bottom': '12px',
          } as React.CSSProperties}
        >
          {/* Seção de Dados Pessoais */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <Form.Item
              name="first_name"
              label="Nome"
              rules={[{ required: true, message: 'Insira o nome' }]}
              style={{ marginBottom: 0 }}
            >
              <Input size="middle" placeholder="João" />
            </Form.Item>
            <Form.Item
              name="last_name"
              label="Sobrenome"
              rules={[{ required: true, message: 'Insira o sobrenome' }]}
              style={{ marginBottom: 0 }}
            >
              <Input size="middle" placeholder="Silva" />
            </Form.Item>
          </div>

          {/* Contato e Acesso */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <Form.Item
              name="email"
              label="E-mail"
              rules={[
                { required: true, message: 'Insira o e-mail' },
                { type: 'email', message: 'E-mail inválido' }
              ]}
              style={{ marginBottom: 0 }}
            >
              <Input size="middle" prefix={<MailOutlined />} placeholder="usuario@email.com" />
            </Form.Item>

            <Form.Item
              name="username"
              label="Usuário"
              rules={[
                { required: true, message: 'Insira o usuário' },
                { pattern: /^[a-zA-Z0-9_.]+$/, message: 'Apenas letras, números, ponto e _' }
              ]}
              style={{ marginBottom: 0 }}
            >
              <Input
                size="middle"
                placeholder="usuario.nome"
                onChange={() => setUsernameWasManuallyChanged(true)}
              />
            </Form.Item>
          </div>

          {/* Senha (apenas criar) */}
          {mode === "create" && (
            <Form.Item
              name="password"
              label="Senha"
              rules={[
                { required: true, message: 'Insira a senha' },
                { min: 6, message: 'A senha deve ter ao menos 6 caracteres' }
              ]}
              style={{ marginBottom: 12 }}
            >
              <Input.Password size="middle" placeholder="••••••••" />
            </Form.Item>
          )}

          {/* Configurações */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <Form.Item
              name="tenant_id"
              label="Empresa"
              style={{ marginBottom: 0 }}
            >
              <Select placeholder="Selecione" allowClear size="middle">
                <Select.Option value="">Sem empresa</Select.Option>
                {tenants.map((tenant) => (
                  <Select.Option key={tenant.id} value={String(tenant.id)}>
                    {tenant.nome}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="role"
              label="Perfil"
              rules={[{ required: true, message: 'Selecione o perfil' }]}
              style={{ marginBottom: 0 }}
            >
              <Select placeholder="Selecione" disabled={roleLocked} size="middle">
                {assignableRoles.map(role => (
                  <Select.Option key={role} value={role}>
                    {getRoleLabel(role)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          {/* Status e Supervisor */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 0 }}>
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Selecione o status' }]}
              style={{ marginBottom: 0 }}
            >
              <Select placeholder="Selecione" size="middle">
                <Select.Option value={UserStatus.ACTIVE}>Ativo</Select.Option>
                <Select.Option value={UserStatus.INACTIVE}>Inativo</Select.Option>
                <Select.Option value={UserStatus.PENDING}>Pendente</Select.Option>
              </Select>
            </Form.Item>

            {(currentRole === UserRole.LIDER_VENDAS || currentRole === UserRole.RELACIONADOR) && (
              <Form.Item
                name="supervisor_id"
                label="Supervisor"
                rules={[{ required: true, message: 'Selecione o supervisor' }]}
                style={{ marginBottom: 0 }}
              >
                <Select
                  placeholder="Selecione"
                  loading={isLoadingSupervisores || isLoadingUserSupervisor}
                  disabled={!currentTenantId}
                  onChange={(value) => setSelectedSupervisor(value ?? null)}
                  size="middle"
                >
                  {supervisores
                    .filter(s => {
                      if (!currentTenantId) return false;
                      return String(s.tenant_id) === String(currentTenantId);
                    })
                    .map((s) => (
                      <Select.Option key={s.id} value={s.id}>
                        {s.nome}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            )}
          </div>

          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }} gap={12}>
              <Button onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="primary"
                loading={loading}
                onClick={handleSubmit}
              >
                {loading
                  ? (mode === "create" ? "Adicionando..." : "Salvando...")
                  : (mode === "create" ? "Adicionar" : "Salvar Alterações")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </UserDialogShell>
    </>
  )
}